import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ToastProvider, useToast } from './contexts/ToastContext'
import AuthPage from './components/AuthPage'
import ProtectedRoute from './components/ProtectedRoute'
import ResponsiveLayout from './components/ResponsiveLayout'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingIndicator } from './components/LoadingIndicator'
import { useEffect, useState } from 'react'
import { isOnline, addNetworkStatusListeners } from './utils/network'
import './App.css'
import './styles/responsive.css'

// Placeholder components - will be implemented in subsequent tasks
function HomePage() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isNetworkConnected, setIsNetworkConnected] = useState(isOnline());
  
  useEffect(() => {
    // Set up network status listeners
    const cleanup = addNetworkStatusListeners(
      () => {
        setIsNetworkConnected(true);
        showToast('You are back online', 'success');
      },
      () => {
        setIsNetworkConnected(false);
        showToast('You are offline. Some features may be limited.', 'warning', 0);
      }
    );
    
    return cleanup;
  }, [showToast]);
  
  const header = (
    <div className="app-header">
      <h1 className="app-title">Proximity Messaging</h1>
      <div className="user-info">
        {!isNetworkConnected && (
          <span className="offline-indicator">Offline</span>
        )}
        <span className="welcome-text d-none d-md-inline">
          Welcome, {user?.displayName}!
        </span>
        {user?.isAnonymous && <span className="guest-badge d-none d-md-inline">(Guest)</span>}
        <button 
          onClick={logout} 
          className="btn-touch btn-touch-sm"
          aria-label="Logout"
        >
          <span className="d-none d-md-inline">Logout</span>
          <span className="d-md-none">👋</span>
        </button>
      </div>
    </div>
  );

  const sidebar = (
    <div className="app-sidebar">
      <div className="sidebar-content">
        <h3>Conversations</h3>
        <p>Your conversations will appear here.</p>
      </div>
    </div>
  );
  
  return (
    <LoadingIndicator isLoading={isLoading}>
      <ResponsiveLayout 
        header={header}
        sidebar={sidebar}
        unreadCount={0}
        className="app-layout"
      >
        <div className="main-content">
          <div className="welcome-section">
            <h2>Welcome to Proximity Messaging!</h2>
            <p>Connect with people nearby and start conversations.</p>
            <div className="feature-grid mobile-grid">
              <div className="feature-card card-mobile">
                <div className="feature-icon">🗺️</div>
                <h3>Interactive Map</h3>
                <p>See who's nearby on an interactive map</p>
              </div>
              <div className="feature-card card-mobile">
                <div className="feature-icon">💬</div>
                <h3>Real-time Chat</h3>
                <p>Message people in your vicinity instantly</p>
              </div>
              <div className="feature-card card-mobile">
                <div className="feature-icon">🔒</div>
                <h3>Privacy Controls</h3>
                <p>Control who can see and message you</p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveLayout>
    </LoadingIndicator>
  )
}

function AppErrorFallback() {
  const navigate = useNavigate();
  
  const handleGoHome = () => {
    navigate('/');
  };
  
  return (
    <div className="error-boundary">
      <div className="error-boundary__content">
        <h2>Something went wrong</h2>
        <p>We're sorry, but something unexpected happened with the application.</p>
        <div className="error-boundary__actions">
          <button 
            onClick={handleGoHome}
            className="error-boundary__retry-btn"
          >
            Go to Home
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="error-boundary__reload-btn"
          >
            Reload Page
          </button>
        </div>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ToastProvider>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </ToastProvider>
      </ErrorBoundary>
    </div>
  )
}

export default App
